import type { TimeSeries, ValueAxisScale, ValueAxisScales } from '../TimeSeriesChart.types';
import { applyValueAxisScale } from './applyValueAxisScale';
import type { ValueRange } from './ValueRange.types';

export type AxisGroupMember = {
  groupId: string;
  minValue: number;
  maxValue: number;
};

const EMPTY_VALUE_RANGE: ValueRange = { min: 0, max: 1 };

export function getAxisGroupId(series: Pick<TimeSeries, 'id' | 'axisGroupId'>): string {
  return series.axisGroupId ?? series.id;
}

function unionRange(range: ValueRange | undefined, { minValue, maxValue }: AxisGroupMember): ValueRange | undefined {
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return range;
  }

  return range
    ? { min: Math.min(range.min, minValue), max: Math.max(range.max, maxValue) }
    : { min: minValue, max: maxValue };
}

function centreFlatRange({ min, max }: ValueRange, forceMin: number | undefined): ValueRange {
  if (max > min) {
    return { min, max };
  }

  const halfSpan = min === 0 ? 1 : Math.abs(min) / 2;

  return { min: Math.max(min - halfSpan, forceMin ?? Number.NEGATIVE_INFINITY), max: min + halfSpan };
}

function shapeRange(dataRange: ValueRange | undefined, scale: ValueAxisScale | undefined): ValueRange {
  return dataRange ? centreFlatRange(applyValueAxisScale(dataRange, scale), scale?.forceMin) : EMPTY_VALUE_RANGE;
}

export function resolveValueAxisRanges(
  members: AxisGroupMember[],
  scales: ValueAxisScales = {}
): Map<string, ValueRange> {
  const dataRanges = members.reduce(
    (ranges, member) => ranges.set(member.groupId, unionRange(ranges.get(member.groupId), member)),
    new Map<string, ValueRange | undefined>()
  );

  return new Map([...dataRanges].map(([groupId, dataRange]) => [groupId, shapeRange(dataRange, scales[groupId])]));
}
